"use client"

import { useRef, useState } from "react"
import type { RefObject } from "react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import type { BooruPost } from "@/lib/booru-api"
import type { SourceMatch } from "@/lib/reverse-search"

// Moved out of components/booru-detail-modal.tsx (same state and functions).

/** Sites that answer a request from another site with a redirect instead of the picture, so a reverse-search engine cannot
 * read their images by link. For these the image is captured through our own page and searched from there. */
const BLOCKS_OTHER_SITES = ["gelbooru.com", "sankakucomplex.com", "pximg.net"]
function imageBlocksOtherSites(url?: string | null): boolean {
  if (!url) return false
  try {
    const host = new URL(url).hostname.toLowerCase()
    return BLOCKS_OTHER_SITES.some((d) => host === d || host.endsWith(`.${d}`))
  } catch {
    return false
  }
}

export function useFrameCapture({
  post,
  isMediaVideo,
  videoRef,
  mediaRootRef,
}: {
  post: BooruPost | null
  isMediaVideo: boolean
  videoRef: RefObject<HTMLVideoElement | null>
  mediaRootRef: RefObject<HTMLDivElement | null>
}) {
  const tt = useTranslations("booruDetailModal")
  // Screenshot / frame capture state
  const [capturedFrameUrl, setCapturedFrameUrl] = useState<string | null>(null) // public blob URL
  const [capturedFrameDataUrl, setCapturedFrameDataUrl] = useState<string | null>(null) // local preview
  const [isCapturing, setIsCapturing] = useState(false)
  const capturedDataUrlRef = useRef<string | null>(null)
  const [findSource, setFindSource] = useState<{ open: boolean; loading: boolean; matches: SourceMatch[]; errors: Record<string, string> }>({ open: false, loading: false, matches: [], errors: {} })
  const capturedBlobUrlRef = useRef<string | null>(null) // track for cleanup

  /** Forget the captured frame (and delete its upload). The modal calls this when it moves to another post. */
  const resetCapture = () => {
    if (capturedBlobUrlRef.current) {
      fetch("/api/screenshot", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: capturedBlobUrlRef.current }),
      }).catch(() => {})
      capturedBlobUrlRef.current = null
    }
    setCapturedFrameUrl(null)
    setCapturedFrameDataUrl(null)
  }

  /** The image to send to a reverse-search engine: the captured frame if there is one, a fresh capture of the current
   * frame for a video (the engines cannot read a video file), or the image itself. */
  const getSearchImageUrl = async (): Promise<string | null> => {
    if (!post) return null
    if (capturedFrameUrl) return capturedFrameUrl
    if (isMediaVideo || imageBlocksOtherSites(post.fileUrl || post.sampleUrl)) return captureFrame(true)
    return post.fileUrl || post.sampleUrl || null
  }

  /** Put a picture on the clipboard (as PNG, the one image type browsers accept there). False when the browser refuses. */
  const copyImageToClipboard = async (dataUrl: string): Promise<boolean> => {
    try {
      if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") return false
      const image = document.createElement("img")
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve()
        image.onerror = () => reject(new Error("image"))
        image.src = dataUrl
      })
      const canvas = document.createElement("canvas")
      canvas.width = image.naturalWidth
      canvas.height = image.naturalHeight
      canvas.getContext("2d")?.drawImage(image, 0, 0)
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"))
      if (!blob) return false
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
      return true
    } catch {
      return false
    }
  }

  const handleReverseSearch = async (service: string) => {
    if (!post) return

    const services: { [key: string]: (url: string) => string } = {
      google: (url) => `https://lens.google.com/uploadbyurl?url=${encodeURIComponent(url)}`,
      saucenao: (url) => `https://saucenao.com/search.php?url=${encodeURIComponent(url)}`,
      iqdb: (url) => `https://iqdb.org/?url=${encodeURIComponent(url)}`,
      // TinEye removed
      // tineye: (url) => `https://www.tineye.com/search?url=${encodeURIComponent(url)}`,
      yandex: (url) =>
        `https://yandex.com/images/search?rpt=imageview&cbir_page=1&url=${encodeURIComponent(url)}`,
      imgops: (url) => `https://imgops.com/${url}`,
    }
    const homes: { [key: string]: string } = {
      google: "https://lens.google.com/",
      saucenao: "https://saucenao.com/",
      iqdb: "https://iqdb.org/",
      yandex: "https://yandex.com/images/",
      imgops: "https://imgops.com/",
    }

    // A video needs its frame captured first, which takes a moment. Open the tab now, while the click still counts,
    // and point it at the engine once the frame is ready (a tab opened after waiting is blocked as a pop-up).
    const needsCapture = isMediaVideo || imageBlocksOtherSites(post.fileUrl || post.sampleUrl)
    const waiting = needsCapture && !capturedFrameUrl ? window.open("about:blank", "_blank") : null
    const imageUrl = await getSearchImageUrl()
    const searchUrl = imageUrl ? services[service]?.(imageUrl) : undefined
    if (!searchUrl) {
      // No link to send (a guest cannot upload, or the upload failed), but the picture was captured: put it on the
      // clipboard and open the engine, so the visitor only has to paste it.
      const local = capturedDataUrlRef.current
      if (local && homes[service] && (await copyImageToClipboard(local))) {
        if (waiting) waiting.location.href = homes[service]
        else window.open(homes[service], "_blank")
        toast.success(tt("imageCopiedPaste"), { duration: 6000 })
        return
      }
      waiting?.close()
      toast.error(tt("failedToCaptureFrame"))
      return
    }
    if (waiting) waiting.location.href = searchUrl
    else window.open(searchUrl, "_blank")
  }

  /** "Find source": IQDB and trace.moe asked at once, shown as one list in a dialog. */
  const handleFindSource = async () => {
    if (!post) return
    setFindSource({ open: true, loading: true, matches: [], errors: {} })
    const imageUrl = await getSearchImageUrl()
    if (!imageUrl) {
      setFindSource({ open: true, loading: false, matches: [], errors: { iqdb: "no image" } })
      return
    }
    try {
      const response = await fetch("/api/booru/reverse-search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageUrl }),
      })
      let data = await response.json()
      // The site refuses to let other sites read this image: search a capture of it instead (once).
      if (data?.errors?.image === "blocked" && !capturedFrameUrl && !imageUrl.includes("blob.vercel-storage.com")) {
        const captured = await captureFrame(true)
        if (captured) {
          const retry = await fetch("/api/booru/reverse-search", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ imageUrl: captured }),
          })
          data = await retry.json()
        }
      }
      setFindSource({ open: true, loading: false, matches: Array.isArray(data.matches) ? data.matches : [], errors: data.errors || {} })
    } catch {
      setFindSource({ open: true, loading: false, matches: [], errors: { iqdb: "request failed" } })
    }
  }

  /**
   * Capture the current media frame (video frame or image) and upload to Vercel Blob.
   * The captured frame is then used for reverse search instead of the original URL.
   */
  const captureFrame = async (silent: boolean): Promise<string | null> => {
    if (!post || isCapturing) return null
    setIsCapturing(true)

    try {
      let dataUrl: string | null = null

      if (isMediaVideo && videoRef.current) {
        // Capture current video frame
        const video = videoRef.current
        const canvas = document.createElement("canvas")
        canvas.width = video.videoWidth || 1280
        canvas.height = video.videoHeight || 720
        const ctx = canvas.getContext("2d")
        if (!ctx) throw new Error(tt("canvasNotSupported"))
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        dataUrl = canvas.toDataURL("image/jpeg", 0.92)
      } else if (mediaRootRef.current) {
        // For images: draw the img element onto a canvas
        const imgEl = mediaRootRef.current.querySelector("img") as HTMLImageElement | null
        if (imgEl) {
          const canvas = document.createElement("canvas")
          const maxDim = 2048
          const scale = Math.min(1, maxDim / Math.max(imgEl.naturalWidth || imgEl.width, imgEl.naturalHeight || imgEl.height))
          canvas.width = (imgEl.naturalWidth || imgEl.width) * scale
          canvas.height = (imgEl.naturalHeight || imgEl.height) * scale
          const ctx = canvas.getContext("2d")
          if (!ctx) throw new Error(tt("canvasNotSupported"))
          ctx.drawImage(imgEl, 0, 0, canvas.width, canvas.height)
          dataUrl = canvas.toDataURL("image/jpeg", 0.92)
        }
      }

      if (!dataUrl) {
        toast.error(tt("couldnTCaptureFrame"))
        return null
      }

      // Store local preview immediately
      setCapturedFrameDataUrl(dataUrl)
      capturedDataUrlRef.current = dataUrl

      // Delete previous captured blob if any
      if (capturedBlobUrlRef.current) {
        fetch("/api/screenshot", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: capturedBlobUrlRef.current }),
        }).catch(() => {})
      }

      // Upload to Vercel Blob for public URL (needed by reverse search services)
      const res = await fetch("/api/screenshot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dataUrl }),
      })

      if (!res.ok) {
        // Guests are refused (401) and storage can be down. The picture is still captured, so keep it: the engines
        // then get it on the clipboard (see copyImageToClipboard) instead of through a link.
        console.warn("[screenshot] Upload refused:", res.status)
        setCapturedFrameUrl(null)
        capturedBlobUrlRef.current = null
        return null
      }
      const { url } = await res.json()

      setCapturedFrameUrl(url)
      capturedBlobUrlRef.current = url
      if (!silent) toast.success(tt("frameCapturedPickASearchEngineToSearch"), { duration: 3000 })
      return url
    } catch (err) {
      console.error("[screenshot] Capture error:", err)
      toast.error(tt("failedToCaptureFrame"))
      setCapturedFrameDataUrl(null)
      setCapturedFrameUrl(null)
      capturedDataUrlRef.current = null
      return null
    } finally {
      setIsCapturing(false)
    }
  }

  const handleCaptureFrame = async () => {
    await captureFrame(false)
  }

  /** Clear captured frame and delete from blob storage */
  const handleClearFrame = () => {
    if (capturedBlobUrlRef.current) {
      fetch("/api/screenshot", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: capturedBlobUrlRef.current }),
      }).catch(() => {})
      capturedBlobUrlRef.current = null
    }
    setCapturedFrameUrl(null)
    setCapturedFrameDataUrl(null)
  }

  return {
    capturedFrameUrl, capturedFrameDataUrl, isCapturing, findSource, setFindSource, captureFrame,
    handleReverseSearch, handleFindSource, handleCaptureFrame, handleClearFrame, resetCapture,
  }
}
